import { ArrowRight, Check, CheckCircle2 } from "lucide-react";
import { PRODUCT_ICONS } from "./sai/productIcons";

// The picture for one step of the intelligence layer's page, ported from sai's
// screens/product/ProductVisual.jsx (brochure-demo @ 2a7c405), the three kinds
// that page uses: a drawn, lightly animated card. Everything is decorative:
// the step's words say the same thing, so it is hidden from screen readers.

// The six parts of the intelligence layer (his data/products.js LAYER).
export const LAYER = [
  { key: "bank", name: "Knowledge bank", sub: "1,500+ biomarkers", icon: "Library" },
  { key: "rules", name: "Rules engine", sub: "Clinical logic", icon: "GitBranch" },
  { key: "mapping", name: "AI biomarker mapping", sub: "Auto-matched to yours", icon: "Shuffle" },
  { key: "portal", name: "Client portal", sub: "Configure & track", icon: "LayoutDashboard" },
  { key: "security", name: "Security", sub: "Encrypted, auditable", icon: "ShieldCheck" },
  { key: "formats", name: "Input formats", sub: "HL7, CSV, XLS, XML, PDF", icon: "FileInput" },
];

function Card({ title, children, wide }) {
  return (
    <div className="pv-card" data-wide={wide ? "true" : "false"}>
      {title ? <p className="pv-card-h">{title}</p> : null}
      {children}
    </div>
  );
}

function Mapping({ rows, formats }) {
  return (
    <Card title="Your codes → our biomarkers">
      {rows.map(([a, b], i) => (
        <div className="pv-map" key={a} style={{ "--i": i }}>
          <code>{a}</code><ArrowRight size={16} /><b>{b}</b><Check size={14} strokeWidth={3} />
        </div>
      ))}
      <div className="pv-formats">{formats.map((f) => <span key={f}>{f}</span>)}</div>
    </Card>
  );
}

// One marker, one approved sentence per state; the visitor's state is lit.
function Explain({ name, value, unit, states, on }) {
  return (
    <Card title="One marker, three approved explanations">
      <div className="pv-ex-top"><b>{name}</b><span><strong>{value}</strong> {unit}</span></div>
      <div className="pv-ex-rows">
        {states.map(([k, t], i) => (
          <div key={k} className="pv-ex-row" data-k={k.toLowerCase()} data-on={i === on ? "true" : "false"} style={{ "--i": i }}>
            <em>{k}</em><span>{t}</span>
          </div>
        ))}
      </div>
      <p className="pv-ex-note"><CheckCircle2 size={14} aria-hidden="true" />Reviewed by doctors, approved once, used in every report</p>
    </Card>
  );
}

// The products on top, the layer under them: its parts, the lit ones in colour.
// (Labels only; nothing links. AI chat (Lumi) stands where he drew AI Video, which is out
// of the flow.)
function Stack({ lit }) {
  const tops = ["Smart Reports", "Viz App", "AI chat", "WhatsApp"];
  return (
    <div className="pv-stack">
      <div className="pv-stack-top">{tops.map((t, i) => <span key={t} style={{ "--i": i }}>{t}</span>)}</div>
      <div className="pv-stack-flow" aria-hidden="true"><i /><i /><i /></div>
      <div className="pv-stack-layer">
        <b>Intelligence layer</b>
        <div className="pv-stack-parts">
          {LAYER.map((part, i) => {
            const Icon = PRODUCT_ICONS[part.icon];
            return (
              <span key={part.key} data-on={lit.includes(part.key) ? "true" : "false"} style={{ "--i": i }}>
                <Icon size={15} aria-hidden="true" />{part.name}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const KINDS = {
  explain: Explain,
  stack: Stack,
  mapping: Mapping,
};

export default function IntelligenceVisual({ visual }) {
  const Kind = KINDS[visual.type];
  return (
    <div className="pv" aria-hidden="true">
      <Kind {...visual} />
    </div>
  );
}
